import { PageCanvas } from "@/components/page-canvas";
import { Form, Head, Link, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import AppLayout from "@/layouts/app_layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  locationDetailingProductsPath,
  locationDetailingProductPath,
  newLocationDetailingProductPath,
} from "@/routes";
import { quantity, type Product } from "./types";

export default function Inventory({
  products,
  low_stock_only,
  query,
  stock_summary,
}: {
  products: Product[];
  low_stock_only: boolean;
  query: string;
  stock_summary: { products: number; empty: number; low: number };
}) {
  const { auth } = usePage().props;
  const { t, i18n } = useTranslation();
  if (!auth.location) return null;
  const location = auth.location;
  return (
    <AppLayout
      breadcrumbs={[
        {
          title: t("inventory.title"),
          href: locationDetailingProductsPath(location.id),
        },
      ]}
    >
      <Head title={t("inventory.title")} />
      <PageCanvas>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h1 className="text-page font-display">{t("inventory.title")}</h1>
          {auth.user?.role === "admin" && (
            <Button
              render={
                <Link href={newLocationDetailingProductPath(location.id)} />
              }
            >
              {t("inventory.add")}
            </Button>
          )}
        </div>
        <p className="text-muted-foreground text-sm">
          {t("inventory.local_help", { location: location.name })}
        </p>
        <dl className="grid gap-4 rounded-lg border p-4 sm:grid-cols-3">
          {(["products", "low", "empty"] as const).map((key) => (
            <div key={key}>
              <dt className="text-sm text-muted-foreground">
                {t(`workflow.stock_${key}`)}
              </dt>
              <dd className="text-xl font-semibold">{stock_summary[key]}</dd>
            </div>
          ))}
        </dl>
        <Form
          action={locationDetailingProductsPath(location.id)}
          method="get"
          className="flex flex-wrap items-end gap-3"
        >
          {low_stock_only && <input type="hidden" name="low_stock" value="1" />}
          <div className="min-w-0 flex-1 space-y-2">
            <Label htmlFor="inventory-search">
              {t("workflow.search_products")}
            </Label>
            <Input id="inventory-search" name="q" defaultValue={query} />
          </div>
          <Button variant="outline" type="submit">
            {t("requests.filter")}
          </Button>
        </Form>
        <nav aria-label={t("inventory.title")} className="flex flex-wrap gap-3">
          <Button
            variant={low_stock_only ? "outline" : "default"}
            render={
              <Link
                href={locationDetailingProductsPath(location.id, { q: query })}
                aria-current={!low_stock_only ? "page" : undefined}
              />
            }
          >
            {t("inventory.all")}
          </Button>
          <Button
            variant={low_stock_only ? "default" : "outline"}
            render={
              <Link
                href={locationDetailingProductsPath(location.id, {
                  low_stock: "1",
                  q: query,
                })}
                aria-current={low_stock_only ? "page" : undefined}
              />
            }
          >
            {t("inventory.low_only")}
          </Button>
        </nav>
        <Card>
          <CardContent className="divide-y">
            {products.length === 0 && (
              <p className="text-muted-foreground py-6">
                {t(low_stock_only ? "inventory.low_empty" : "inventory.empty")}
              </p>
            )}
            {products.map((product) => (
              <article key={product.id} className="space-y-3 py-5">
                <div className="flex flex-wrap items-center gap-3">
                  <Link
                    className="rounded-sm font-semibold break-words underline underline-offset-4 focus-visible:ring-2"
                    href={locationDetailingProductPath(location.id, product.id)}
                  >
                    {product.name}
                  </Link>
                  {product.low_stock && (
                    <Badge variant="secondary">{t("inventory.low")}</Badge>
                  )}
                </div>
                <p className="text-sm">
                  {t("inventory.balance")}:{" "}
                  {quantity(product.quantity, i18n.language)}{" "}
                  {t(`inventory.${product.unit}`)}
                </p>
                <p className="text-muted-foreground text-sm">
                  {t("inventory.threshold")}:{" "}
                  {product.threshold === null
                    ? t("inventory.disabled")
                    : `${quantity(product.threshold, i18n.language)} ${t(`inventory.${product.unit}`)}`}
                </p>
              </article>
            ))}
          </CardContent>
        </Card>
      </PageCanvas>
    </AppLayout>
  );
}
